import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, Plus } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { inr } from "@/lib/api";
import StarRating from "@/components/StarRating";

export default function ProductCard({ product, index = 0 }) {
  const { addToCart, toggleWishlist, isWishlisted } = useStore();
  const wished = isWishlisted(product.id);

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, delay: (index % 4) * 0.08 }}
      className="group relative"
      data-testid={`product-card-${product.slug}`}
    >
      <div className="perspective">
        <div
          className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-b from-[#0f1b15] to-[#0a0a0a] p-5 transition-all duration-500 group-hover:border-[var(--card-accent)]/50 group-hover:-translate-y-2"
          style={{ "--card-accent": product.accent }}
        >
          {/* accent glow on hover */}
          <div
            className="pointer-events-none absolute -inset-10 opacity-0 blur-3xl transition-opacity duration-500 group-hover:opacity-30"
            style={{ background: `radial-gradient(circle at 50% 30%, ${product.accent}, transparent 60%)` }}
          />

          {/* wishlist */}
          <button
            onClick={() => toggleWishlist(product)}
            data-testid={`wishlist-toggle-${product.slug}`}
            aria-label="Add to wishlist"
            className="absolute right-4 top-4 z-20 grid h-9 w-9 place-items-center rounded-full glass transition-transform hover:scale-110"
          >
            <Heart size={16} className={wished ? "fill-red-500 text-red-500" : "text-white/70"} />
          </button>

          {(product.high_protein || product.iron_focused) && (
            <div className="absolute left-4 top-4 z-20 flex flex-col items-start gap-1.5">
              {product.high_protein && (
                <span data-testid={`high-protein-badge-${product.slug}`}
                  className="rounded-full gold-gradient px-3 py-1 font-oswald text-[10px] font-semibold uppercase tracking-[0.12em] text-black shadow-lg">
                  High Protein
                </span>
              )}
              {product.iron_focused && (
                <span data-testid={`iron-focused-badge-${product.slug}`}
                  className="rounded-full border border-gold/60 bg-black/70 px-3 py-1 font-oswald text-[10px] font-semibold uppercase tracking-[0.12em] text-gold backdrop-blur">
                  Iron-Focused
                </span>
              )}
            </div>
          )}

          <Link to={`/product/${product.slug}`} className="relative z-10 block">
            <div className="relative mx-auto aspect-square w-full overflow-hidden rounded-2xl">
              <motion.img
                src={product.image}
                alt={`${product.name} — Dignity Food Kitchen ${product.flavour} snack jar`}
                loading="lazy"
                className="h-full w-full object-cover product-shadow transition-transform duration-700 group-hover:scale-105"
                style={{ transformStyle: "preserve-3d" }}
              />
            </div>
          </Link>

          <div className="relative z-10 mt-5">
            <span
              className="font-oswald text-[11px] uppercase tracking-[0.2em]"
              style={{ color: product.accent }}
            >
              {product.flavour}
            </span>
            <Link to={`/product/${product.slug}`}>
              <h3 className="mt-1 font-oswald text-lg font-semibold leading-tight text-cream hover:text-gold transition-colors">
                {product.name}
              </h3>
            </Link>
            <p className="mt-1 text-sm text-white/50 line-clamp-2">{product.short_desc}</p>

            <div className="mt-3 flex items-center gap-2">
              <StarRating rating={product.rating} />
              <span className="text-xs text-white/40">
                {product.rating} · {product.reviews_count}
              </span>
            </div>

            <div className="mt-4 flex items-end justify-between">
              <div>
                <div className="flex items-baseline gap-2">
                  <span className="font-oswald text-2xl text-cream">{inr(product.price)}</span>
                  <span className="text-sm text-white/30 line-through">{inr(product.mrp)}</span>
                </div>
                <span className="text-xs text-white/40">{product.weight}</span>
              </div>
              <button
                onClick={() => addToCart(product, 1)}
                data-testid={`quick-add-${product.slug}`}
                aria-label={`Add ${product.name} to cart`}
                className="flex items-center gap-1 rounded-full gold-gradient px-4 py-2 font-oswald text-xs uppercase tracking-wider text-black transition-transform hover:scale-105"
              >
                <Plus size={14} /> Add
              </button>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
